<template>
  <section id="inicio" class="min-h-screen flex items-center justify-center relative overflow-hidden gradient-hero pt-24 md:pt-0">
    <!-- Technological Network Background -->
    <div class="absolute inset-0 overflow-hidden opacity-20">
      <!-- Connection Lines -->
      <svg class="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="hsl(var(--primary))" stop-opacity="0.3" />
            <stop offset="100%" stop-color="hsl(var(--primary))" stop-opacity="0.1" />
          </linearGradient>
        </defs>
        <!-- Animated connecting lines -->
        <path d="M150,200 Q400,100 650,300 T900,150" stroke="url(#lineGradient)" stroke-width="2" fill="none" class="animate-pulse" />
        <path d="M100,400 Q350,250 600,450 T850,350" stroke="url(#lineGradient)" stroke-width="2" fill="none" class="animate-pulse" style="animation-delay: 1s" />
        <path d="M200,600 Q450,400 700,650 T950,500" stroke="url(#lineGradient)" stroke-width="2" fill="none" class="animate-pulse" style="animation-delay: 2s" />
      </svg>

      <!-- Floating Tech Icons -->
      <div class="absolute top-20 left-20 animate-float">
        <div class="p-4 bg-primary/10 rounded-full backdrop-blur-sm border border-primary/20">
          <Cloud class="w-8 h-8 text-primary" />
        </div>
      </div>
      
      <div class="absolute top-40 right-32 animate-float" style="animation-delay: 1s">
        <div class="p-4 bg-primary/10 rounded-full backdrop-blur-sm border border-primary/20">
          <Server class="w-8 h-8 text-primary" />
        </div>
      </div>
      
      <div class="absolute bottom-40 left-32 animate-float" style="animation-delay: 2s">
        <div class="p-4 bg-primary/10 rounded-full backdrop-blur-sm border border-primary/20">
          <Database class="w-8 h-8 text-primary" />
        </div>
      </div>
      
      <div class="absolute bottom-20 right-20 animate-float" style="animation-delay: 3s">
        <div class="p-4 bg-primary/10 rounded-full backdrop-blur-sm border border-primary/20">
          <Globe class="w-8 h-8 text-primary" />
        </div>
      </div>
      
      <div class="absolute top-1/2 left-10 animate-float" style="animation-delay: 0.5s">
        <div class="p-4 bg-primary/10 rounded-full backdrop-blur-sm border border-primary/20">
          <Network class="w-8 h-8 text-primary" />
        </div>
      </div>
      
      <div class="absolute top-1/2 right-10 animate-float" style="animation-delay: 1.5s">
        <div class="p-4 bg-primary/10 rounded-full backdrop-blur-sm border border-primary/20">
          <Shield class="w-8 h-8 text-primary" />
        </div>
      </div>

      <!-- Network Nodes -->
      <div class="absolute top-32 left-1/2 w-3 h-3 bg-primary rounded-full animate-pulse"></div>
      <div class="absolute bottom-32 left-1/3 w-2 h-2 bg-primary/60 rounded-full animate-pulse" style="animation-delay: 1s"></div>
      <div class="absolute top-2/3 right-1/4 w-4 h-4 bg-primary/40 rounded-full animate-pulse" style="animation-delay: 2s"></div>
      <div class="absolute bottom-1/2 right-1/3 w-2 h-2 bg-primary/80 rounded-full animate-pulse" style="animation-delay: 0.5s"></div>

      <!-- Gradient overlays for depth -->
      <div class="absolute -top-4 -right-4 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
      <div class="absolute -bottom-8 -left-8 w-96 h-96 bg-forza-navy/30 rounded-full blur-3xl"></div>
    </div>

    <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div class="max-w-4xl mx-auto">
        <!-- Badge -->
        <div class="inline-flex items-center px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-8 animate-fade-in">
          <Star class="w-4 h-4 mr-2" />
          20+ años de experiencia de nuestros socios
        </div>

        <!-- Main heading -->
        <h1 class="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight animate-fade-in">
          Transformamos tu
          <span class="text-gradient">TI</span>
          en ventaja
          <span class="text-gradient">competitiva</span>
        </h1>

        <!-- Subtitle -->
        <p class="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in">
          Soluciones tecnológicas a medida para Empresas Mexicanas que buscan innovar, 
          optimizar procesos y acelerar su crecimiento digital.
        </p>

        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
          <button 
            @click="scrollToContact"
            class="bg-primary hover:bg-primary/90 text-primary-foreground font-medium px-6 py-2 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl group text-base flex items-center justify-center"
          >
            Contáctanos
            <ArrowRight class="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          
          <button 
            @click="scrollToServices"
            class="px-6 py-2 text-base border border-primary/20 hover:bg-primary/10 rounded-lg transition-colors duration-300"
          >
            Conoce nuestros servicios
          </button>
        </div>

        <!-- Stats -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 animate-fade-in">
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-primary mb-2">20+</div>
            <div class="text-muted-foreground">Años de experiencia</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-primary mb-2">50+</div>
            <div class="text-muted-foreground">Proyectos exitosos</div>
          </div>
          <div class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-primary mb-2">95%</div>
            <div class="text-muted-foreground">Satisfacción del cliente</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
      <div class="w-6 h-10 border-2 border-primary/50 rounded-full flex justify-center">
        <div class="w-1 h-3 bg-primary rounded-full mt-2 animate-pulse"></div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ArrowRight, Star, Cloud, Server, Globe, Network, Database, Shield } from 'lucide-vue-next'

const scrollToContact = () => {
  const element = document.querySelector("#contacto")
  if (element) {
    element.scrollIntoView({ behavior: "smooth" })
  }
}

const scrollToServices = () => {
  const element = document.querySelector("#servicios")
  if (element) {
    element.scrollIntoView({ behavior: "smooth" })
  }
}
</script>